// Capture a traffic notice: pick the vehicle, attach the photo or PDF, and type
// (or check) what it says. The document reader may fill the form, and every
// field it filled says so, because a figure read by a machine is a proposal
// until a person has looked at the notice. Nothing reaches the queue until Save,
// which runs fine_create_v1: the duplicate guard, the attribution, the owner.
//
// A file attached and then abandoned is removed on Cancel, so private storage
// does not fill with notices nobody saved.
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { FilePlus2, Paperclip } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Spinner } from '../../lib/shadcn/spinner'
import { useCreateFine, useDiscardUpload, useGetEvidenceUrl, useUploadEvidence } from '../../hooks/backend/fines'
import { samePlate, toOffenceIso, type FineDetail, type Options, type Reading } from '../../utils/fines'
import { CATEGORY_OPTIONS } from '../../utils/drivingFines'

const MAX_BYTES = 15 * 1024 * 1024

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve((typeof reader.result === 'string' ? reader.result : '').split(',')[1] ?? '')
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

type Form = {
  vehicle_id: string
  notice_number: string
  issuing_authority: string
  offence_date: string
  offence_time: string
  location: string
  offence_description: string
  amount_zar: string
  discount_deadline: string
  notes: string
  offence_category: string
  recorded_kmh: string
  limit_kmh: string
}

const EMPTY: Form = {
  vehicle_id: '', notice_number: '', issuing_authority: '', offence_date: '', offence_time: '',
  location: '', offence_description: '', amount_zar: '', discount_deadline: '', notes: '',
  offence_category: '', recorded_kmh: '', limit_kmh: '',
}

type Upload = { storage_path: string; mime_type: string; bytes: number; reading: Reading | null; filename: string }

function Field({ label, read, children, htmlFor }: { label: string; read?: boolean; children: ReactNode; htmlFor: string }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="flex items-center gap-2 text-xs font-medium text-foreground">
        {label}
        {read && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-100">read from the notice, check it</span>}
      </label>
      {children}
    </div>
  )
}

export function CaptureFineDialog({
  open,
  onOpenChange,
  options,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: Options | null
  onSaved: (d: FineDetail) => void
}) {
  const upload = useUploadEvidence<Omit<Upload, 'filename'>>()
  const discard = useDiscardUpload<{ removed: boolean }>()
  const preview = useGetEvidenceUrl<{ url: string }>()
  const create = useCreateFine<{ saved: boolean; needs_confirmation?: boolean; warning?: string; id?: string; detail?: FineDetail }>()

  const [form, setForm] = useState<Form>(EMPTY)
  const [readFields, setReadFields] = useState<Set<keyof Form>>(new Set())
  const [doRead, setDoRead] = useState(true)
  const [file, setFile] = useState<Upload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setForm(EMPTY)
    setReadFields(new Set())
    setFile(null)
    setError(null)
    setDuplicateWarning(null)
  }, [open])

  const vehicle = useMemo(() => options?.vehicles.find((v) => v.id === form.vehicle_id) ?? null, [options, form.vehicle_id])
  const plateRead = file?.reading?.fields.registration ?? null
  const plateMismatch = !!(vehicle && plateRead && !samePlate(vehicle.registration, plateRead))

  const set = (k: keyof Form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }))
    setReadFields((s) => {
      if (!s.has(k)) return s
      const n = new Set(s)
      n.delete(k)
      return n
    })
    setDuplicateWarning(null)
  }

  const onFile = async (f: File | null) => {
    setError(null)
    if (!f) return
    if (!form.vehicle_id) {
      setError('Pick the vehicle first, so the notice is kept with it.')
      return
    }
    if (f.size > MAX_BYTES) {
      setError('That file is over 15 MB. Take the photo again at a lower size, or scan the notice to PDF.')
      return
    }
    try {
      if (file) await discard.trigger({ storage_path: file.storage_path }).catch(() => undefined)
      const b64 = await readAsBase64(f)
      const r = await upload.trigger({ vehicle_id: form.vehicle_id, filename: f.name, contentBase64: b64, read: doRead })
      if (!r) return
      setFile({ ...r, filename: f.name })
      const fields = r.reading?.fields
      if (fields && r.reading?.method === 'vision') {
        const filled = new Set<keyof Form>()
        const next = { ...form }
        const put = (k: keyof Form, v: string | number | null) => {
          if (v === null || v === undefined || String(v) === '' || next[k]) return
          next[k] = String(v)
          filled.add(k)
        }
        put('notice_number', fields.notice_number)
        put('issuing_authority', fields.issuing_authority)
        put('offence_date', fields.offence_date)
        put('offence_time', fields.offence_time)
        put('location', fields.location)
        put('offence_description', fields.offence_description)
        put('amount_zar', fields.amount_zar)
        put('discount_deadline', fields.discount_deadline)
        setForm(next)
        setReadFields(filled)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const openPreview = async () => {
    if (!file) return
    try {
      const r = await preview.trigger({ storage_path: file.storage_path }, { skipCache: true })
      if (r?.url) window.open(r.url, '_blank', 'noopener')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const cancel = async () => {
    if (file) await discard.trigger({ storage_path: file.storage_path }).catch(() => undefined)
    onOpenChange(false)
  }

  const offenceAt = toOffenceIso(form.offence_date, form.offence_time)
  const amount = form.amount_zar.trim() === '' ? NaN : Number(form.amount_zar.replace(/[R\s,]/g, ''))
  const ready = !!form.vehicle_id && !!offenceAt && Number.isFinite(amount) && amount >= 0

  const save = async (confirmDuplicate: boolean) => {
    setError(null)
    try {
      const r = await create.trigger({
        vehicle_id: form.vehicle_id,
        notice_number: form.notice_number || null,
        issuing_authority: form.issuing_authority || null,
        offence_at: offenceAt,
        location: form.location || null,
        offence_description: form.offence_description || null,
        amount_zar: amount,
        discount_deadline: form.discount_deadline || null,
        evidence_path: file?.storage_path ?? null,
        notes: form.notes || null,
        // 0481. Left empty, the database proposes a category from the offence
        // text and says so; speeds only on a speeding notice.
        offence_category: form.offence_category || null,
        recorded_kmh: form.offence_category === 'speeding' && form.recorded_kmh ? form.recorded_kmh : null,
        limit_kmh: form.offence_category === 'speeding' && form.limit_kmh ? form.limit_kmh : null,
        extracted_fields: file?.reading?.method === 'vision'
          ? { ...file.reading.fields, model: file.reading.model, confirmed_fields: [...readFields] }
          : null,
        confirm_possible_duplicate: confirmDuplicate,
      })
      if (r?.needs_confirmation) {
        setDuplicateWarning(r.warning ?? 'There is already a notice at this date and time on this vehicle.')
        return
      }
      if (r?.saved && r.detail) {
        setFile(null)
        onSaved(r.detail)
        onOpenChange(false)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const busy = upload.loading || create.loading

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : void cancel())}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Capture a traffic notice</DialogTitle>
          <DialogDescription>
            Enter what the notice says. When you save, the queue proposes who was driving from the roster and finds the owner.
            Nobody's name goes on a fine until a manager confirms it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Vehicle" htmlFor="fine-vehicle">
            <NativeSelect id="fine-vehicle" value={form.vehicle_id} onChange={(e) => set('vehicle_id', e.target.value)} disabled={!!file}>
              <option value="">Choose the registration on the notice…</option>
              {(options?.vehicles ?? []).map((v) => (
                <option key={v.id} value={v.id}>{v.label}{v.owner_name ? `, ${v.owner_name}` : ''}</option>
              ))}
            </NativeSelect>
            {file && <p className="text-[11px] text-muted-foreground">The vehicle is fixed once a file is attached, because the file is stored with it. Cancel to start again.</p>}
          </Field>

          <div className="space-y-2 rounded-md border border-dashed border-border p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
                <Paperclip className="h-4 w-4" />
                {file ? 'Replace the photo or PDF' : 'Attach a photo or PDF of the notice'}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="sr-only"
                  disabled={busy}
                  onChange={(e) => { void onFile(e.target.files?.[0] ?? null); e.currentTarget.value = '' }}
                />
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={doRead} onCheckedChange={(v) => setDoRead(v === true)} disabled={!!file || busy} />
                Read the notice and fill the form for me to check
              </label>
              {upload.loading && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> {doRead ? 'Storing and reading…' : 'Storing…'}</span>}
            </div>
            {file && (
              <p className="text-xs text-muted-foreground">
                Attached {file.filename}.{' '}
                <button type="button" className="underline" onClick={() => void openPreview()}>Look at it</button>
              </p>
            )}
            {file?.reading?.warnings.map((w, i) => <p key={i} className="text-xs text-amber-800 dark:text-amber-200">{w}</p>)}
            {plateMismatch && (
              <p className="text-xs font-medium text-rose-700 dark:text-rose-300">
                The notice reads {plateRead}, but you chose {vehicle?.registration}. Check the vehicle before saving.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Notice number" htmlFor="fine-notice" read={readFields.has('notice_number')}>
              <Input id="fine-notice" value={form.notice_number} onChange={(e) => set('notice_number', e.target.value)} />
            </Field>
            <Field label="Issuing authority" htmlFor="fine-authority" read={readFields.has('issuing_authority')}>
              <Input id="fine-authority" value={form.issuing_authority} onChange={(e) => set('issuing_authority', e.target.value)} placeholder="For example JMPD, TMPD, RTMC" />
            </Field>
            <Field label="Offence date" htmlFor="fine-date" read={readFields.has('offence_date')}>
              <Input id="fine-date" type="date" value={form.offence_date} onChange={(e) => set('offence_date', e.target.value)} />
            </Field>
            <Field label="Offence time" htmlFor="fine-time" read={readFields.has('offence_time')}>
              <Input id="fine-time" type="time" value={form.offence_time} onChange={(e) => set('offence_time', e.target.value)} />
            </Field>
            <Field label="Location" htmlFor="fine-location" read={readFields.has('location')}>
              <Input id="fine-location" value={form.location} onChange={(e) => set('location', e.target.value)} />
            </Field>
            <Field label="Amount on the notice (full, not discounted)" htmlFor="fine-amount" read={readFields.has('amount_zar')}>
              <Input id="fine-amount" inputMode="decimal" value={form.amount_zar} onChange={(e) => set('amount_zar', e.target.value)} placeholder="R" />
            </Field>
            <Field label="Discount deadline" htmlFor="fine-discount" read={readFields.has('discount_deadline')}>
              <Input id="fine-discount" type="date" value={form.discount_deadline} onChange={(e) => set('discount_deadline', e.target.value)} />
            </Field>
            <Field label="Offence" htmlFor="fine-description" read={readFields.has('offence_description')}>
              <Input id="fine-description" value={form.offence_description} onChange={(e) => set('offence_description', e.target.value)} placeholder="For example speeding, 78 in a 60 zone" />
            </Field>
            <Field label="What the notice is for" htmlFor="fine-category">
              <NativeSelect id="fine-category" value={form.offence_category} onChange={(e) => set('offence_category', e.target.value)}>
                <option value="">Read it from the offence text</option>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            {form.offence_category === 'speeding' && (
              <>
                <Field label="Recorded speed on the notice, km/h" htmlFor="fine-recorded">
                  <Input id="fine-recorded" inputMode="numeric" value={form.recorded_kmh} onChange={(e) => set('recorded_kmh', e.target.value)} />
                </Field>
                <Field label="Limit on the notice, km/h" htmlFor="fine-limit">
                  <Input id="fine-limit" inputMode="numeric" value={form.limit_kmh} onChange={(e) => set('limit_kmh', e.target.value)} />
                </Field>
              </>
            )}
          </div>
          {form.offence_category === 'speeding' && (
            <p className="text-xs text-muted-foreground">
              Both speeds decide whether this counts as a breach week or as reckless driving (clauses 21.6A and 21.5). A speeding
              fine without them waits on the Driving conduct page until they are entered.
            </p>
          )}
          <Field label="Notes" htmlFor="fine-notes">
            <Textarea id="fine-notes" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </Field>

          {!offenceAt && (form.offence_date || form.offence_time) && (
            <p className="text-xs text-muted-foreground">Enter both the date and the time. The time decides which shift the notice falls in.</p>
          )}
          {duplicateWarning && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">{duplicateWarning}</p>
          )}
          {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="ghost" onClick={() => void cancel()} disabled={create.loading}>Cancel</Button>
            {duplicateWarning ? (
              <Button variant="warning" disabled={!ready || busy} onClick={() => void save(true)}>Save, it is a different notice</Button>
            ) : (
              <Button variant="brand" disabled={!ready || busy} onClick={() => void save(false)}>
                {create.loading ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <FilePlus2 className="mr-1 h-4 w-4" />}
                Save to the queue
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
